import React, { useMemo } from "react";
import { ToastContainer, toast  } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  exportPaymentsExcel,
  exportPaymentsPdf,
} from "./utils/paymentsExportUtils";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { usePaymentsDashboard } from "./hooks/usePaymentsDashboard";

import DashboardPaymentsHeader from "./components/DashboardPaymentsHeader";
import PaymentsKpiCards from "./components/PaymentsKpiCards";
import PaymentsFiltersCard from "./components/PaymentsFiltersCard";
import PaymentsTable from "./components/PaymentsTable";
import PaymentDetailsDrawer from "./components/PaymentDetailsDrawer";

import PaymentBoutiqueDrawer from "./components/PaymentBoutiqueDrawer";
import PaymentSpecialDrawer from "./components/PaymentSpecialDrawer";
import PaymentEditDrawer from "./components/PaymentEditDrawer";
import PaymentReminderHistoryDrawer from "./components/PaymentReminderHistoryDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedPaymentsPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Paiements & règlements
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PaymentsDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const dashboard = usePaymentsDashboard();

  const handleExport = (format) => {
  try {
    const rows = dashboard.filteredPayments;

    if (!rows.length) {
      toast.warn("Aucun paiement à exporter.");
      return;
    }

    const season = dashboard.filters.season;

    if (format === "excel") {
      exportPaymentsExcel(rows, season);
    } else if (format === "pdf") {
      exportPaymentsPdf(rows, season);
    }

    toast.success(
      `Export ${format === "excel" ? "Excel" : "PDF"} généré.`
    );
  } catch (error) {
    console.error("Erreur export paiements :", error);
    toast.error("Impossible de générer l'export.");
  }
};

  if (roleId !== 1) {
    return <UnauthorizedPaymentsPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardPaymentsHeader
          helpOpen={dashboard.helpOpen}
          loading={dashboard.loading}
          onCreateBoutique={dashboard.openCreateBoutiqueModal}
          onCreateSpecial={dashboard.openCreateSpecialModal}
          onToggleHelp={dashboard.toggleHelp}
          onRefresh={dashboard.fetchPayments}
        />

        <PaymentsKpiCards
          stats={dashboard.stats}
          filteredStats={dashboard.filteredStats}
          loading={dashboard.loading}
          selectedSeason={dashboard.selectedSeason}
        />

        <PaymentsFiltersCard
          filters={dashboard.filters}
          setFilter={dashboard.setFilter}
          resetFilters={dashboard.resetFilters}
          toggleStatusFilter={dashboard.toggleStatusFilter}
          selectAllStatuses={dashboard.selectAllStatuses}
          clearStatusFilters={dashboard.clearStatusFilters}
          articleOptions={dashboard.articleOptions}
          seasons={dashboard.seasons}
          filteredCount={dashboard.filteredPayments.length}
          totalCount={dashboard.payments.length}
          loading={dashboard.loading}
          onRefresh={dashboard.fetchPayments}
          onExport={handleExport}
        />

        <PaymentsTable
          payments={dashboard.filteredPayments}
          loading={dashboard.loading}
          adherentsByUser={dashboard.adherentsByUser}
          deletingId={dashboard.deletingId}
          remindingId={dashboard.remindingId}
          togglingAuthorizationId={dashboard.togglingAuthorizationId}
          onToggleAuthorization={dashboard.toggleAuthorization}
          onSendReminder={dashboard.sendReminder}
          onEdit={dashboard.openEditModal}
          onDelete={dashboard.deletePayment}
          onOpenDetails={dashboard.openDetailsDrawer}
          onOpenReminderHistory={dashboard.openReminderHistoryDrawer}
        />
      </div>

      <PaymentDetailsDrawer
        isOpen={dashboard.detailsDrawerOpen}
        payment={dashboard.detailsPayment}
        deletingId={dashboard.deletingId}
        remindingId={dashboard.remindingId}
        togglingAuthorizationId={dashboard.togglingAuthorizationId}
        onClose={dashboard.closeDetailsDrawer}
        onReminder={dashboard.sendReminder}
        onEdit={(payment) => {
          dashboard.closeDetailsDrawer();
          dashboard.openEditModal(payment);
        }}
        onDelete={dashboard.deletePayment}
        onToggleAuthorization={dashboard.toggleAuthorization}
        onOpenReminderHistory={dashboard.openReminderHistoryDrawer}
      />

      <PaymentReminderHistoryDrawer
        isOpen={dashboard.reminderHistoryDrawerOpen}
        payment={dashboard.reminderHistoryPayment}
        onClose={dashboard.closeReminderHistoryDrawer}
      />

      <PaymentBoutiqueDrawer
        isOpen={dashboard.createBoutiqueModalOpen}
        onClose={dashboard.closeCreateBoutiqueModal}
        onSaved={dashboard.fetchPayments}
      />

      <PaymentSpecialDrawer
        isOpen={dashboard.createSpecialModalOpen}
        onClose={dashboard.closeCreateSpecialModal}
        onSaved={dashboard.fetchPayments}
      />

      <PaymentEditDrawer
        isOpen={dashboard.editModalOpen}
        payment={dashboard.editablePayment}
        onClose={dashboard.closeEditModal}
        onSaved={dashboard.fetchPayments}
      />

      <ToastContainer />
    </section>
  );
}
